import React, { useState, useMemo } from "react";
import { useAtom } from "jotai";
import { userAtom, devicesAtom } from "../../jotai/atoms";
import { Squirrel } from "../../components/spinner/Squirrel";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useTranslation } from "react-i18next";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { registerPushPermission } from "../../pushNotifications";
import { updateDevicesAtom } from "../../jotai/atomUpdater";
import Bugsnag from "@bugsnag/js";

enum NotificationType {
  ALL,
  EMAIL_RECORD_UPDATE,
  EMAIL_REMINDER,
  EMAIL_APP_VERSION,
  PUSH_RECORD_UPDATE,
  PUSH_REMINDER,
  PUSH_APP_VERSION,
}

export default function MobileNotifications() {
  const { t } = useTranslation();
  const [user, setUser] = useAtom(userAtom);
  const [updating, setUpdating] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string | boolean>(false);
  const [devices, setDevices] = useAtom(devicesAtom);

  const showPushNotifications = useMemo(() => {
    return Capacitor.isNativePlatform();
  }, []);

  if (!user || updating) {
    return (
      <div className="max-md:min-h-screen pt-20">
        <Squirrel />
      </div>
    );
  }

  const toggleNotification = async (notificationType: NotificationType) => {
    let newPreferences = {
      ...(notificationType === NotificationType.ALL
        ? {
            disable_all: !user.notification_preferences?.disable_all,
            email_new_results: false,
            email_upcoming_vaccines: false,
            email_app_updates: false,
            push_new_results: false,
            push_upcoming_vaccines: false,
            push_app_updates: false,
          }
        : {}),
      ...(notificationType === NotificationType.EMAIL_RECORD_UPDATE
        ? { email_new_results: !user.notification_preferences?.email_new_results }
        : {}),
      ...(notificationType === NotificationType.EMAIL_REMINDER
        ? { email_upcoming_vaccines: !user.notification_preferences?.email_upcoming_vaccines }
        : {}),
      ...(notificationType === NotificationType.EMAIL_APP_VERSION
        ? { email_app_updates: !user.notification_preferences?.email_app_updates }
        : {}),
      ...(notificationType === NotificationType.PUSH_RECORD_UPDATE
        ? { push_new_results: !user.notification_preferences?.push_new_results }
        : {}),
      ...(notificationType === NotificationType.PUSH_REMINDER
        ? { push_upcoming_vaccines: !user.notification_preferences?.push_upcoming_vaccines }
        : {}),
      ...(notificationType === NotificationType.PUSH_APP_VERSION
        ? { push_app_updates: !user.notification_preferences?.push_app_updates }
        : {}),
    };

    try {
      setUpdating(true);
      await getAPIClient().updateUser({ notification_preferences: newPreferences });
      let userResults = await getAPIClient().getUser();
      if (userResults) {
        await setUser(userResults);
      }
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    }

    const enabledPushNotification =
      (notificationType === NotificationType.PUSH_RECORD_UPDATE &&
        newPreferences.push_new_results) ||
      (notificationType === NotificationType.PUSH_REMINDER &&
        newPreferences.push_upcoming_vaccines) ||
      (notificationType === NotificationType.PUSH_APP_VERSION && newPreferences.push_app_updates);

    if (Capacitor.isNativePlatform() && enabledPushNotification) {
      await registerPushPermission();
    }

    setUpdating(false);
  };

  const onDeleteDeviceClick = async (deviceId: string) => {
    const device = devices.find((device) => device.device_id === deviceId);
    const { value } = await Dialog.confirm({
      title: "Delete Device",
      message: `Are you sure you want to delete ${device?.name}?`,
    });

    if (value) {
      try {
        await getAPIClient().deleteDevice(deviceId);
        await updateDevicesAtom();
      } catch (e: unknown) {
        if (e instanceof DocketAPIError) {
          setShowErrorMessage("There was an error deleting the device. Please try again later.");
          Bugsnag.notify(e as Error);
        }
      }
    }
  };

  const allDisabled = user.notification_preferences?.disable_all ?? false;

  return (
    <div className="mt-15 flex flex-col gap-2 mx-6">
      {showErrorMessage && (
        <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>{showErrorMessage}</ErrorModal>
      )}
      <label className="fieldset-label mobile-text-color ml-4">
        <input
          className="checkbox mobile-checkbox mr-6 checked:text-white"
          data-testid="consent-checkbox"
          type="checkbox"
          name={t("settings.notif_prefs_disable")}
          checked={allDisabled}
          onChange={() => toggleNotification(NotificationType.ALL)}
        />
        {t("settings.notif_prefs_disable")}
      </label>
      <p className="text-black mobile-text-color italic text-sm ml-4">
        {t("settings.notif_disable_message")}
      </p>
      <div className="flex flex-col gap-1">
        <div className="flex flex-col">
          <p className="text-black mobile-text-color font-semibold">
            {t("settings.notif_prefs_email")}
          </p>
          <div className="border-b-[1px] text-black mobile-text-color"></div>
        </div>
        <div className="flex flex-col gap-1 ml-4">
          <label
            className={`fieldset-label mobile-text-color ${allDisabled ? "!text-gray-400" : ""}`}
          >
            <input
              className="checkbox mobile-checkbox mr-6 checked:text-white"
              data-testid="consent-checkbox"
              type="checkbox"
              name={t("settings.notif_immunization_records_updates")}
              checked={user.notification_preferences?.email_new_results ?? false}
              onChange={() => toggleNotification(NotificationType.EMAIL_RECORD_UPDATE)}
              disabled={allDisabled}
            />
            {t("settings.notif_immunization_records_updates")}
          </label>
          <label
            className={`fieldset-label mobile-text-color ${allDisabled ? "!text-gray-400" : ""}`}
          >
            <input
              className="checkbox mobile-checkbox mr-6 checked:text-white"
              data-testid="consent-checkbox"
              type="checkbox"
              name={t("settings.notif_immunization_reminders")}
              checked={user.notification_preferences?.email_upcoming_vaccines ?? false}
              onChange={() => toggleNotification(NotificationType.EMAIL_REMINDER)}
              disabled={allDisabled}
            />
            {t("settings.notif_immunization_reminders")}
          </label>
          <label
            className={`fieldset-label mobile-text-color ${allDisabled ? "!text-gray-400" : ""}`}
          >
            <input
              className="checkbox mobile-checkbox mr-6 checked:text-white"
              data-testid="consent-checkbox"
              type="checkbox"
              name={t("settings.notif_immunization_versions")}
              checked={user.notification_preferences?.email_app_updates ?? false}
              onChange={() => toggleNotification(NotificationType.EMAIL_APP_VERSION)}
              disabled={allDisabled}
            />
            {t("settings.notif_immunization_versions")}
          </label>
        </div>
      </div>
      {showPushNotifications && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-col">
            <p className="text-black mobile-text-color font-semibold">
              {t("settings.notif_prefs_push")}
            </p>
            <div className="border-b-[1px] text-black mobile-text-color"></div>
          </div>
          <div className="flex flex-col gap-1 ml-4">
            <label
              className={`fieldset-label mobile-text-color ${allDisabled ? "!text-gray-400" : ""}`}
            >
              <input
                className="checkbox mobile-checkbox mr-6 checked:text-white"
                data-testid="consent-checkbox"
                type="checkbox"
                name={t("settings.notif_immunization_records_updates")}
                checked={user.notification_preferences?.push_new_results ?? false}
                onChange={() => toggleNotification(NotificationType.PUSH_RECORD_UPDATE)}
                disabled={allDisabled}
              />
              {t("settings.notif_immunization_records_updates")}
            </label>
            <label
              className={`fieldset-label mobile-text-color ${allDisabled ? "!text-gray-400" : ""}`}
            >
              <input
                className="checkbox mobile-checkbox mr-6 checked:text-white"
                data-testid="consent-checkbox"
                type="checkbox"
                name={t("settings.notif_immunization_reminders")}
                checked={user.notification_preferences?.push_upcoming_vaccines ?? false}
                onChange={() => toggleNotification(NotificationType.PUSH_REMINDER)}
                disabled={allDisabled}
              />
              {t("settings.notif_immunization_reminders")}
            </label>
            <label
              className={`fieldset-label mobile-text-color ${allDisabled ? "!text-gray-400" : ""}`}
            >
              <input
                className="checkbox mobile-checkbox mr-6 checked:text-white"
                data-testid="consent-checkbox"
                type="checkbox"
                name={t("settings.notif_immunization_versions")}
                checked={user.notification_preferences?.push_app_updates ?? false}
                onChange={() => toggleNotification(NotificationType.PUSH_APP_VERSION)}
                disabled={allDisabled}
              />
              {t("settings.notif_immunization_versions")}
            </label>
          </div>
        </div>
      )}
      {showPushNotifications && devices.length > 0 && (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex flex-col">
              <p className="text-black mobile-text-color font-semibold">
                {t("settings.notif_prefs_devices")}
              </p>
              <p className="text-black text-sm mobile-text-color italic">
                {t("settings.notif_prefs_devices_message")}
              </p>
              <div className="border-b-[1px] text-black mobile-text-color"></div>
            </div>
            <div className="flex flex-col gap-1 ml-4">
              {devices.map((device) => (
                <label className="fieldset-label mobile-text-color">
                  <input
                    className="checkbox mobile-checkbox mr-6 checked:text-white"
                    type="checkbox"
                    name={device.name}
                    checked={true}
                    onChange={() => onDeleteDeviceClick(device.device_id)}
                  />
                  {device.name}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
